import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";

const ORANGE = "#F37021";
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.gif";

const formatSize = bytes => {
  if (!bytes) return "—";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// O arquivo é servido por /public (autenticado pelo cookie de sessão), então
// basta abrir a URL numa aba nova — o próprio navegador manda o cookie.
const fileUrl = relPath =>
  `${getBackendUrl()}/public/${relPath.split("/").map(encodeURIComponent).join("/")}`;

const TriagemMedia = () => {
  const [files, setFiles] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingRuleId, setSavingRuleId] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [selectedPaths, setSelectedPaths] = useState([]);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/keyword-rules/media");
      setFiles(data.files || []);
      setRules(data.rules || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const libraryFiles = useMemo(() => files.filter(f => !f.legacy), [files]);
  const legacyFiles = useMemo(() => files.filter(f => f.legacy), [files]);
  const fileByPath = useMemo(() => {
    const map = {};
    files.forEach(f => { map[f.path] = f; });
    return map;
  }, [files]);

  const handleUpload = async event => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;
    setUploading(true);
    try {
      for (const file of selected) {
        const formData = new FormData();
        formData.append("file", file);
        await api.post("/keyword-rules/media", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      toast.success(selected.length > 1 ? "Arquivos enviados." : "Arquivo enviado.");
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async file => {
    const emUso = file.usedBy.length > 0;
    const aviso = emUso
      ? `"${file.name}" está vinculado a ${file.usedBy.length} regra(s): ${file.usedBy
          .map(r => r.keyword)
          .join(", ")}.\n\nExcluir o arquivo e remover esse anexo das regras?`
      : `Excluir "${file.name}" definitivamente?`;
    if (!window.confirm(aviso)) return;

    try {
      await api.delete(
        `/keyword-rules/media/${encodeURIComponent(file.name)}${emUso ? "?force=1" : ""}`
      );
      toast.success("Arquivo excluído.");
      await load();
    } catch (err) {
      toastError(err);
    }
  };

  const openRuleDialog = rule => {
    setEditingRule(rule);
    setSelectedPaths(rule.mediaPaths);
  };

  const togglePath = path => {
    setSelectedPaths(prev =>
      prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]
    );
  };

  const handleSaveRuleMedia = async () => {
    setSavingRuleId(editingRule.id);
    try {
      await api.put(`/keyword-rules/${editingRule.id}/media`, { paths: selectedPaths });
      toast.success("Anexos da regra atualizados.");
      setEditingRule(null);
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingRuleId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Arquivos que o bot de triagem anexa às respostas automáticas (catálogo de
        modelos, kits, receituários). Envie aqui e escolha, mais abaixo, quais
        regras enviam cada um.
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          style={{ backgroundColor: ORANGE, color: "#fff" }}
        >
          {uploading ? "Enviando..." : "Enviar arquivo"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple
          accept={ACCEPT}
          onChange={handleUpload}
        />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          PDF ou imagem, até 50 MB por arquivo.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Arquivo</TableCell>
              <TableCell align="right">Tamanho</TableCell>
              <TableCell>Usado nas regras</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {libraryFiles.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  Nenhum arquivo na biblioteca ainda.
                </TableCell>
              </TableRow>
            )}
            {libraryFiles.map(file => (
              <TableRow key={file.path} hover>
                <TableCell>{file.name}</TableCell>
                <TableCell align="right">{formatSize(file.size)}</TableCell>
                <TableCell>
                  {file.usedBy.length === 0 ? (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      não usado
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {file.usedBy.map(r => (
                        <Chip key={r.id} label={r.keyword} size="small" variant="outlined" />
                      ))}
                    </Box>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Abrir">
                    <IconButton
                      size="small"
                      onClick={() => window.open(fileUrl(file.path), "_blank", "noopener")}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton size="small" onClick={() => handleDelete(file)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {legacyFiles.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Anexos antigos, salvos fora desta biblioteca. Continuam sendo enviados
            normalmente; podem ser desvinculados na regra, mas não são excluídos por aqui.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
            {legacyFiles.map(file => (
              <Chip
                key={file.path}
                size="small"
                label={file.missing ? `${file.name} (arquivo sumiu)` : file.name}
                color={file.missing ? "error" : "default"}
                variant="outlined"
                onClick={() => !file.missing && window.open(fileUrl(file.path), "_blank", "noopener")}
              />
            ))}
          </Box>
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#273A74", mb: 1.5 }}>
        Regras do bot e seus anexos
      </Typography>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Palavra-chave</TableCell>
              <TableCell>Anexos enviados (em ordem)</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map(rule => (
              <TableRow key={rule.id} hover>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip label={rule.keyword} size="small" variant="outlined" />
                    {!rule.isActive && (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        inativa
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {rule.mediaPaths.length === 0 ? (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      só texto
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {rule.mediaPaths.map(p => (
                        <Chip
                          key={p}
                          size="small"
                          label={fileByPath[p]?.name || p}
                          color={fileByPath[p]?.missing ? "error" : "default"}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    startIcon={<AttachFileIcon fontSize="small" />}
                    onClick={() => openRuleDialog(rule)}
                    disabled={savingRuleId === rule.id}
                  >
                    Anexos
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!editingRule} onClose={() => setEditingRule(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          Anexos da regra &quot;{editingRule?.keyword}&quot;
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Os arquivos marcados são enviados ANTES do texto da resposta, na ordem
            em que aparecem aqui.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
            {libraryFiles.length === 0 && (
              <Typography variant="body2" sx={{ color: "text.secondary", py: 2 }}>
                Envie um arquivo para a biblioteca primeiro.
              </Typography>
            )}
            {libraryFiles.map(file => (
              <FormControlLabel
                key={file.path}
                control={
                  <Checkbox
                    checked={selectedPaths.includes(file.path)}
                    onChange={() => togglePath(file.path)}
                    style={{ color: ORANGE }}
                  />
                }
                label={<Typography variant="body2">{file.name}</Typography>}
              />
            ))}
            {selectedPaths
              .filter(p => fileByPath[p]?.legacy)
              .map(p => (
                <FormControlLabel
                  key={p}
                  control={
                    <Checkbox
                      checked
                      onChange={() => togglePath(p)}
                      style={{ color: ORANGE }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {fileByPath[p]?.name || p}{" "}
                      <Typography component="span" variant="caption" sx={{ color: "text.secondary" }}>
                        (anexo antigo — desmarcar remove o vínculo)
                      </Typography>
                    </Typography>
                  }
                />
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingRule(null)}>Cancelar</Button>
          <Button
            variant="contained"
            style={{ backgroundColor: ORANGE, color: "#fff" }}
            onClick={handleSaveRuleMedia}
            disabled={savingRuleId !== null}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TriagemMedia;
